import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { parseTradeSuccessMessage } from "utils/parseTradeSuccessMessage";

/**
 * MT5-style full-page trade confirmation (mobile UI only).
 * Header symbol + detail line (buy/sell colored) + green "Done" + bottom OK.
 */
export default function Mt5TradeDonePage({
  message,
  symbol: symbolProp,
  side: sideProp,
  qty: qtyProp,
  price: priceProp,
  ticket: ticketProp,
  error = false,
  onClose,
}) {
  const parsed = parseTradeSuccessMessage(message);
  const ticket = ticketProp || parsed.ticket;
  const symbol = symbolProp || parsed.symbol;
  const side = (sideProp || parsed.side || "").toLowerCase();
  const qty = qtyProp || parsed.qty;
  const price = priceProp || parsed.price;
  const hasStructured = Boolean(symbol && side && qty && price);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("fx-mt5-fullpage-open");
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.classList.remove("fx-mt5-fullpage-open");
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`mt5-trade-done${error ? " mt5-trade-done--error" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={error ? "Order error" : "Trade confirmation"}
    >
      <header className="mt5-trade-done__head">
        <span className="mt5-trade-done__symbol">{symbol || " "}</span>
        <button
          type="button"
          className="mt5-trade-done__dismiss"
          aria-label="Close"
          onClick={onClose}
        >
          ✕
        </button>
      </header>

      <div className="mt5-trade-done__body">
        {error ? (
          <p className="mt5-trade-done__detail mt5-trade-done__detail--raw">
            {parsed.raw || message || "Could not place order."}
          </p>
        ) : hasStructured ? (
          <p className="mt5-trade-done__detail">
            {ticket ? (
              <>
                <span className="mt5-trade-done__ticket">#{ticket}</span>{" "}
              </>
            ) : null}
            <span
              className={
                side === "sell" ? "mt5-trade-done__side--sell" : "mt5-trade-done__side--buy"
              }
            >
              {side}
            </span>{" "}
            <span>{qty}</span> <span>{symbol}</span> at <span>{price}</span>
          </p>
        ) : (
          <p className="mt5-trade-done__detail mt5-trade-done__detail--raw">
            {parsed.raw || "Done"}
          </p>
        )}
        {error ? null : <p className="mt5-trade-done__status">Done</p>}
      </div>

      <button type="button" className="mt5-trade-done__ok" onClick={onClose}>
        OK
      </button>
    </div>,
    document.body
  );
}
